import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Lock, Unlock, ShieldCheck, Sparkles, Filter } from "lucide-react";

const VERTICALS = ["casino", "case_opening"];
const DEFAULT_FILTERS = { window: 90, tier: "", bracket: "", casinoId: "", network: "", recentlyJoined: false, sort: "volume", page: 1, pageSize: 12 };

function Chip({ active, onClick, children, testId, disabled }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`px-3 h-8 border font-mono text-[11px] uppercase tracking-widest transition-colors ${active ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]" : "border-white/15 text-[#E8ECF4] hover:bg-white/5"} disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function VerticalTab({ value, active, onClick, disabled, children }) {
  return (
    <button
      data-testid={`vertical-tab-${value}`}
      onClick={onClick}
      disabled={disabled}
      className={`px-5 h-11 border-b-2 font-mono text-xs uppercase tracking-widest transition-colors ${active ? "border-[#2DD4A7] text-[#E8ECF4]" : "border-transparent text-[#8B97AE] hover:text-[#E8ECF4]"} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function ConsentModal({ open, player, vertical, restrictionVersion, onCancel, onConfirmed, t, locale }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  // Iter 146 — Refund matrix fetched from /config/chat-refunds so the
  // r6 copy interpolates the SAME fractions the settlement engine uses.
  const [refundCfg, setRefundCfg] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/config/chat-refunds").then((r) => setRefundCfg(r.data)).catch(() => {});
  }, [open]);
  // Reset the checkbox EVERY time the modal opens for a new attempt — never
  // remembered across attempts (spec: "re-shown on every attempt").
  useEffect(() => { if (open) { setChecked(false); setBusy(false); } }, [open, player?.id, vertical]);
  if (!open || !player) return null;
  const pricing = player.pricing || {};
  const charged = pricing.chatUnlockCharged ?? player.unlockCostCredits ?? 0;
  const remainder = pricing.contractRemainder ?? 0;
  const capBinds = !!pricing.capBinds;

  // Interpolate {B}/{A}/{BY_PLAYER}/{BY_STREAMER}/{SEVEN_DAY}/{DAYS} into r6.
  // Falls back to raw template while the fetch is in-flight — never a wrong
  // number, just an un-substituted placeholder for the flicker window.
  const pct = (v) => (typeof v === "number" ? Math.round(v * 100) : "—");
  const applyRefundTokens = (raw) => {
    if (!refundCfg) return raw;
    const f = refundCfg.fractions || {};
    return raw
      .replaceAll("{B}",           String(pct(f.signed_elsewhere)))
      .replaceAll("{A}",           String(pct(f.no_engagement_approved)))
      .replaceAll("{BY_PLAYER}",   String(pct(f.by_player)))
      .replaceAll("{BY_STREAMER}", String(pct(f.by_streamer)))
      .replaceAll("{SEVEN_DAY}",   String(pct(f.seven_day_no_deal)))
      .replaceAll("{DAYS}",        String(refundCfg.pre_contract_lifetime_days ?? "—"));
  };

  const submit = async () => {
    if (!checked) return;
    setBusy(true);
    try {
      await api.post(`/marketplace/unlock/${player.id}`, {
        vertical,
        consent: true,
        restrictionVersion,
      });
      // Server currently returns 402 for the payment stub — treat any 2xx defensively as success as well.
      toast.success(t("mkt.consent.paymentNext"));
      onConfirmed();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        // Payment step stub — consent already recorded server-side.
        toast.success(typeof detail === "object" ? t("mkt.consent.paymentNext") : (detail || t("mkt.consent.paymentNext")));
        onConfirmed();
        return;
      }
      // 403 / 409 / anything else: DO NOT open chat, show the server message.
      toast.error(apiErrorMessage(e, t("mkt.consent.failed")));
    } finally {
      setBusy(false);
    }
  };

  const restrictions = [
    { id: "r1", text: t(vertical === "case_opening" ? "mkt.consent.r1.case_opening" : "mkt.consent.r1.casino") },
    { id: "r2", text: t("mkt.consent.r2") },
    { id: "r3", text: t("mkt.consent.r3") },
    { id: "r4", text: t("mkt.consent.r4") },
    { id: "r5", text: t("mkt.consent.r5") },
    { id: "r6", text: applyRefundTokens(t("mkt.consent.r6")) },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
      data-testid="unlock-consent-modal"
      role="dialog"
      aria-modal="true"
      // Do NOT close on backdrop click — the modal is unskippable. The only
      // exits are Cancel or Confirm (with checkbox).
    >
      <div className="bg-[#0B0E14] border border-white/15 max-w-2xl w-full mt-12" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5B841] mb-2">
            {t(`mkt.vertical.${vertical}`)} · {player.name}
          </div>
          <h2 className="font-display text-2xl font-black tracking-tighter">{t("mkt.consent.title")}</h2>
          <p className="text-sm text-[#8B97AE] mt-2">{t("mkt.consent.lead")}</p>
        </div>
        <ol className="p-5 space-y-3 list-decimal pl-9 text-sm text-[#E8ECF4]">
          {restrictions.map((r) => (
            <li key={r.id} data-testid={`consent-${r.id}`} className="leading-relaxed">
              {r.text}
            </li>
          ))}
          <li data-testid="consent-cost" className="leading-relaxed">
            This unlock costs <span className="font-mono text-[#F5B841] font-semibold" data-testid="consent-cost-value">{charged.toLocaleString()} credits</span>{capBinds && (
              <span className="text-[#8B97AE]"> — {locale === "de"
                ? <>+ Rest von <span className="font-mono text-[#E8ECF4]" data-testid="consent-remainder-value">{remainder.toLocaleString()}</span> fällig bei Vertragsabschluss</>
                : locale === "es"
                ? <>+ resto de <span className="font-mono text-[#E8ECF4]" data-testid="consent-remainder-value">{remainder.toLocaleString()}</span> al firmar el contrato</>
                : <>+ remainder of <span className="font-mono text-[#E8ECF4]" data-testid="consent-remainder-value">{remainder.toLocaleString()}</span> due at contract formation</>}</span>
            )} (payment gated — no credits will be deducted in this preview).
          </li>
        </ol>
        <div className="px-5 py-4 border-t border-white/10 flex items-start gap-3">
          <Checkbox
            id="consent-checkbox"
            data-testid="consent-checkbox"
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            className="mt-0.5 border-white/40 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:border-[#2DD4A7]"
          />
          <label htmlFor="consent-checkbox" className="text-sm text-[#E8ECF4] leading-snug select-none cursor-pointer">
            {t("mkt.consent.checkbox")}
          </label>
        </div>
        <div className="p-5 border-t border-white/10 flex items-center justify-end gap-3">
          <Button
            data-testid="consent-cancel"
            onClick={onCancel}
            disabled={busy}
            variant="ghost"
            className="rounded-none border border-white/15 h-10 px-4 text-[#E8ECF4] hover:bg-white/10 transition-colors"
          >
            {t("mkt.consent.cancel")}
          </Button>
          <Button
            data-testid="consent-confirm"
            onClick={submit}
            disabled={!checked || busy}
            className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 px-5 font-semibold transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            {busy ? "…" : t("mkt.consent.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Marketplace() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const isAdmin = user?.role === "admin";
  const [meta, setMeta] = useState(null);
  const [vertical, setVertical] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState(DEFAULT_FILTERS);
  const [consentPlayer, setConsentPlayer] = useState(null); // player object or null
  const [unlockedIds, setUnlockedIds] = useState(new Set()); // client-side hint after 402

  // Which verticals are visible in the tab bar. Server is the source of truth
  // (verticalsEnabled) — but admins additionally see case_opening in the UI
  // even before the global launch flip so they can QA it in preview.
  const visibleVerticals = useMemo(() => {
    if (!meta) return [];
    const enabled = new Set(meta.verticalsEnabled || []);
    const out = [];
    if (enabled.has("casino") || isAdmin) out.push("casino");
    if (enabled.has("case_opening") || isAdmin) out.push("case_opening");
    return out;
  }, [meta, isAdmin]);

  // A tab is only clickable if the caller is actually enabled for it on the
  // server. Admins can click anything for QA.
  const canUseVertical = useCallback((v) => {
    if (!meta) return false;
    if (isAdmin) return true;
    return (meta.verticalsEnabled || []).includes(v);
  }, [meta, isAdmin]);

  useEffect(() => {
    api.get("/marketplace/meta")
      .then((r) => {
        setMeta(r.data);
        const enabled = r.data.verticalsEnabled || [];
        // Default vertical: first enabled for streamers, casino for admins.
        const initial = enabled[0] || (isAdmin ? "casino" : null);
        setVertical(initial);
      })
      .catch((e) => setError(apiErrorMessage(e, "Access denied")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!vertical) return;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      params.set("vertical", vertical);
      Object.entries(f).forEach(([k, v]) => {
        if (v === "" || v === false || v === null || v === undefined) return;
        // Suppress filters that don't apply to case_opening — the frontend
        // never sends them so cached casino state can't bleed across a switch.
        if (vertical === "case_opening" && ["tier", "casinoId", "network"].includes(k)) return;
        params.set(k, v);
      });
      const { data } = await api.get(`/marketplace/players?${params.toString()}`);
      setData(data);
    } catch (e) {
      setError(apiErrorMessage(e, "Marketplace not available"));
      setData(null);
    } finally { setLoading(false); }
  }, [f, vertical]);

  useEffect(() => { if (meta && vertical) load(); }, [meta, vertical, load]);

  const set = (k, v) => setF((old) => ({ ...old, [k]: v, page: k === "page" ? v : 1 }));

  const switchVertical = (v) => {
    if (!canUseVertical(v) || v === vertical) return;
    // Never merge or cache across verticals. Reset filters, dump data.
    setData(null);
    setF(DEFAULT_FILTERS);
    setVertical(v);
  };

  // Global "no vertical enabled" state — server says the streamer has zero
  // verticals available. Admins never see this because they always have both.
  if (meta && !isAdmin && visibleVerticals.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24" data-testid="marketplace-no-vertical">
        <div className="border border-[#F5B841]/40 bg-[#F5B841]/5 p-8">
          <Lock className="w-8 h-8 text-[#F5B841] mb-4" />
          <h1 className="font-display text-3xl font-black tracking-tighter mb-3">{t("mkt.locked.title")}</h1>
          <p className="text-sm text-[#8B97AE]">{t("mkt.locked.noVertical")}</p>
          <Button asChild className="mt-6 rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-11 px-5 font-semibold transition-colors">
            <Link to="/streamer">{t("common.backToDashboard")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (error && !meta) return (
    <div className="max-w-3xl mx-auto px-6 py-24" data-testid="marketplace-forbidden">
      <div className="border border-[#F26D6D]/40 bg-[#F26D6D]/5 p-8">
        <Lock className="w-8 h-8 text-[#F26D6D] mb-4" />
        <h1 className="font-display text-3xl font-black tracking-tighter mb-3">{t("mkt.locked.title")}</h1>
        <p className="text-sm text-[#8B97AE]">{error}</p>
        <Button asChild className="mt-6 rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-11 px-5 font-semibold transition-colors">
          <Link to="/streamer">{t("common.backToDashboard")}</Link>
        </Button>
      </div>
    </div>
  );

  if (!meta || !vertical) return <div className="p-10 text-[#8B97AE]" data-testid="marketplace-loading">{t("common.loading")}</div>;

  const showCaseAdminHint = vertical === "case_opening" && isAdmin && meta.caseLaunchEnabled === false;
  const showFilters = {
    tier: vertical === "casino",
    casino: vertical === "casino",
    network: vertical === "casino",
    recentlyJoined: true,
    window: true,
    sort: true,
    bracket: true,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7] mb-2">Marketplace</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter" data-testid="marketplace-title">
          {t(`mkt.title.${vertical}`)}
        </h1>
        <p className="text-sm text-[#8B97AE] mt-3 max-w-2xl" data-testid="marketplace-sub">
          {t(`mkt.sub.${vertical}`)}
        </p>
      </div>

      {/* Vertical tab switch — never rendered as a chrome that implies access
          the streamer doesn't have. A streamer enabled for only one vertical
          sees only that one tab. Admins always see both for QA. */}
      {visibleVerticals.length > 1 && (
        <div className="border-b border-white/10 mb-6 flex items-center gap-2" data-testid="vertical-tabs">
          {visibleVerticals.map((v) => (
            <VerticalTab
              key={v}
              value={v}
              active={vertical === v}
              disabled={!canUseVertical(v)}
              onClick={() => switchVertical(v)}
            >
              {t(`mkt.vertical.${v}`)}
            </VerticalTab>
          ))}
        </div>
      )}
      {showCaseAdminHint && (
        <div className="mb-6 border border-[#F5B841]/40 bg-[#F5B841]/5 px-4 py-3 text-xs font-mono uppercase tracking-widest text-[#F5B841]" data-testid="case-not-launched-hint">
          {t("mkt.caseNotLaunched")}
        </div>
      )}

      {/* iter 51 — Window / Tier / Network filters removed. Vertical
          selection is realised via the tabs above (single-vertical view
          per the four compliance rules; no "any" merge to avoid crossing
          gating). Filters retained: Volume, Casino (casino vertical only),
          Sort, Recently-joined. */}
      <div className="border border-white/8 p-4 mb-6 space-y-4" data-testid="marketplace-filters">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]"><Filter className="w-3.5 h-3.5" />Filters</div>
        {showFilters.bracket && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] w-20">Volume</span>
            <Chip testId="brk-any" active={f.bracket === ""} onClick={() => set("bracket", "")}>Any</Chip>
            {meta.brackets.map((b) => <Chip key={b.value} testId={`brk-${b.value}`} active={f.bracket === b.value} onClick={() => set("bracket", b.value)}>{b.label}</Chip>)}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          {showFilters.casino && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">Casino</div>
              <Select value={f.casinoId || "all"} onValueChange={(v) => set("casinoId", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-casino" className="rounded-none bg-transparent border-white/20 h-10"><SelectValue placeholder="Any casino" /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">
                  <SelectItem value="all">Any casino</SelectItem>
                  {meta.casinos.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {showFilters.sort && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">Sort</div>
              <Select value={f.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger data-testid="filter-sort" className="rounded-none bg-transparent border-white/20 h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">
                  <SelectItem value="volume">Highest volume</SelectItem>
                  <SelectItem value="recent">Most recent</SelectItem>
                  <SelectItem value="active">Most active</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {showFilters.recentlyJoined && (
          <label className="flex items-center gap-3 text-sm text-[#8B97AE]">
            <Switch data-testid="filter-recent" checked={f.recentlyJoined} onCheckedChange={(v) => set("recentlyJoined", v)} className="data-[state=checked]:bg-[#2DD4A7]" />
            Recently joined (last 30 days)
          </label>
        )}
      </div>

      {/* Grid */}
      {loading && <div className="text-[#8B97AE]" data-testid="marketplace-loading">Loading…</div>}
      {!loading && data && data.items.length === 0 && (
        <div className="border border-white/8 p-10 text-center text-[#8B97AE]" data-testid={`marketplace-empty-${vertical}`}>
          {t(`mkt.empty.${vertical}`)}
        </div>
      )}
      {!loading && data && data.items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`marketplace-grid-${vertical}`}>
          {data.items.map((c) => {
            const alreadyUnlocked = !c.available || unlockedIds.has(c.id);
            return (
            <article key={c.id} className="border border-white/8 bg-[#151B28]/60 p-5 flex flex-col gap-4" data-testid={`card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-2xl font-bold tracking-tight" data-testid={`alias-${c.id}`}>{c.name}</div>
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest">
                    {c.verificationTier === "vip" ? (
                      <span className="inline-flex items-center gap-1 border border-[#F5B841]/50 text-[#F5B841] px-2 py-0.5"><Sparkles className="w-3 h-3" />VIP</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 border border-emerald-500/50 text-emerald-400 px-2 py-0.5"><ShieldCheck className="w-3 h-3" />Verified</span>
                    )}
                    {/* Iter 161 — Prior-deal chip. Only present when the
                        streamer has a completed / cancelled iter3 contract
                        with this player in the current vertical. Cancelled
                        implies the player pulled the plug (streamers can't
                        cancel iter3). Muted styling — informational, not a
                        blocker. */}
                    {c.priorContract?.status === "completed" && (
                      <span className="inline-flex items-center gap-1 border border-[#8B97AE]/40 text-[#8B97AE] px-2 py-0.5" data-testid={`prior-deal-completed-${c.id}`}>
                        {locale === "de" ? "Voriger Deal · abgeschlossen" : locale === "es" ? "Trato anterior · completado" : "Prior deal — completed"}
                      </span>
                    )}
                    {c.priorContract?.status === "cancelled" && (
                      <span className="inline-flex items-center gap-1 border border-[#F26D6D]/40 text-[#F26D6D] px-2 py-0.5" data-testid={`prior-deal-cancelled-${c.id}`}>
                        {locale === "de" ? "Voriger Deal · vom Spieler storniert" : locale === "es" ? "Trato anterior · cancelado por el jugador" : "Prior deal — cancelled by player"}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${alreadyUnlocked ? "border-[#F5B841]/50 text-[#F5B841]" : "border-[#2DD4A7]/50 text-[#2DD4A7]"}`}>{alreadyUnlocked ? "In contact" : "Available"}</span>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{locale === "de" ? "Verifiziertes Volumen (Admin-geprüft) · Nachweis" : locale === "es" ? "Volumen verificado por administrador · prueba" : "Admin-verified volume · proof"}</div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="font-display text-2xl font-black tracking-tight tabular-nums text-[#8B97AE]" data-testid={`vol-ref-${c.id}`}>{(c.referenceVolumeConfirmed || 0).toLocaleString()}</span>
                  {(c.referenceVolumeConfirmed || 0) > 0 && (
                    <span
                      data-testid={`vol-ref-period-${c.id}`}
                      className="text-[11px] font-mono uppercase tracking-widest text-[#2DD4A7] border border-[#2DD4A7]/50 bg-[#2DD4A7]/10 px-2 py-1 font-semibold"
                    >{locale === "de" ? "verifiziert" : locale === "es" ? "verificado" : "verified"} · {Number(c.wagerReportPeriodDays) === 30 ? t("card.wager.window30") : t("card.wager.window90")}</span>
                  )}
                </div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">{locale === "de" ? "Angebotenes Volumen · Für 90 Tage" : locale === "es" ? "Volumen ofrecido · por 90 días" : "Offered volume · for 90 days"}</div>
                <div className="font-display text-3xl font-black tracking-tight tabular text-[#E8ECF4]" data-testid={`vol-${c.id}`}>{(c.offeredVolume || 0).toLocaleString()}</div>
                {c.hasActiveReferrals && (
                  <div className="mt-2 text-[10px] font-mono text-[#8B97AE]">
                    Under-code · <span className="text-[#E8ECF4]">30d {(c.underCodeVolume.d30||0).toLocaleString()}</span> · <span className="text-[#E8ECF4]">90d {(c.underCodeVolume.d90||0).toLocaleString()}</span> · <span className="text-[#E8ECF4]">180d {(c.underCodeVolume.d180||0).toLocaleString()}</span>
                  </div>
                )}
              </div>
              {/* Iter 127 — pre-unlock current-site + preferred-indicator row.
                  Renders for BOTH verticals (casino tab: current casino;
                  case-pack tab: current case site). The outer marketplace
                  `vertical` filter drives which side renders — earlier the
                  whole block was gated to `casino`, hiding the case row that
                  Iter 126 added. Casino-only chrome (referral tag pills +
                  preferredCasino line) stays gated to the casino tab. */}
              <div className="flex flex-wrap gap-1.5">
                {vertical === "case_opening" ? (
                  c.currentCaseSite
                    ? <span className="text-[10px] font-mono text-[#E8ECF4]" data-testid="pre-current-case">Case: {c.currentCaseSite}</span>
                    : <span className="text-[10px] font-mono text-[#8B97AE]" data-testid="pre-current-case-none">Case: None</span>
                ) : (
                  c.currentCasino
                    ? <span className="text-[10px] font-mono text-[#E8ECF4]" data-testid="pre-current-casino">Casino: {c.currentCasino}</span>
                    : <span className="text-[10px] font-mono text-[#8B97AE]" data-testid="pre-current-casino-none">Casino: None</span>
                )}
                {vertical === "case_opening"
                  ? (c.preferredSiteIndicator?.case_opening?.openToAny && <span className="ml-3 text-[9px] font-mono uppercase tracking-widest text-[#F5B841] border border-[#F5B841]/40 px-1.5" data-testid="pre-open-to-any">Open to any</span>)
                  : (c.preferredSiteIndicator?.casino?.openToAny && <span className="ml-3 text-[9px] font-mono uppercase tracking-widest text-[#F5B841] border border-[#F5B841]/40 px-1.5" data-testid="pre-open-to-any">Open to any</span>)
                }
                {vertical === "case_opening"
                  ? (c.preferredSiteIndicator?.case_opening?.hasPreferred && <span className="ml-1 text-[9px] font-mono uppercase tracking-widest text-[#2DD4A7] border border-[#2DD4A7]/40 px-1.5" data-testid="pre-has-preferred">Has preferred site</span>)
                  : (c.preferredSiteIndicator?.casino?.hasPreferred && <span className="ml-1 text-[9px] font-mono uppercase tracking-widest text-[#2DD4A7] border border-[#2DD4A7]/40 px-1.5" data-testid="pre-has-preferred">Has preferred site</span>)
                }
                {vertical === "casino" && c.casinos.length === 0 && !c.currentCasino && <span className="text-[10px] font-mono text-[#8B97AE]">no active site</span>}
                {vertical === "casino" && c.casinos.map((k) => <span key={k} className="text-[10px] font-mono uppercase tracking-widest border border-white/15 text-[#E8ECF4] px-2 py-0.5">{k}</span>)}
              </div>
              {vertical === "casino" && c.preferredCasino && <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Wants: <span className="text-[#E8ECF4]">{c.preferredCasino}</span></div>}
              {c.streamerPreferences?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {c.streamerPreferences.map((p) => <span key={p} className="text-[10px] font-mono uppercase tracking-widest border border-[#2DD4A7]/40 text-[#2DD4A7] px-2 py-0.5">{p}</span>)}
                </div>
              )}
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                Joined <span data-testid={`joined-${c.id}`} className="text-[#E8ECF4]">{c.joinedAt || c.joinedYearMonth}</span>
              </div>
              {/* Iter 193 — Period + origin chips. Route through the 5 shared
                  i18n keys (see i18n.jsx card.wager.*). Casino total_only uses
                  "total ×0.45"; case total_only just says "total"; categorized
                  uses "categorized" for both. Period is a small trailing chip. */}
              <div className="flex flex-wrap gap-1.5" data-testid={`card-report-meta-${c.id}`}>
                <span
                  data-testid={`card-report-period-${c.id}`}
                  className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] border border-white/15 px-1.5 py-0.5"
                >{Number(c.wagerReportPeriodDays) === 30 ? t("card.wager.period30") : t("card.wager.period90")}</span>
                <span
                  data-testid={`card-report-origin-${c.id}`}
                  className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] border border-white/15 px-1.5 py-0.5"
                >{
                  c.wagerReportMode === "total_only"
                    ? (c.vertical === "case_opening"
                        ? t("card.wager.originLumpCase")
                        : t("card.wager.originLump"))
                    : t("card.wager.originCategorized")
                }</span>
              </div>
              {/* iter 51 — "More details" expander (non-PII only) */}
              <details className="border-t border-white/10 pt-2" data-testid={`more-details-${c.id}`}>
                <summary className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] cursor-pointer hover:text-white transition-colors select-none">More details</summary>
                <div className="mt-3 space-y-2 text-[11px] font-mono">
                  <div className="flex items-baseline justify-between border-b border-white/5 py-1"><span className="text-[#8B97AE]">Vertical</span><span className="text-[#E8ECF4]">{c.vertical}</span></div>
                  <div className="flex items-baseline justify-between border-b border-white/5 py-1"><span className="text-[#8B97AE]">Joined</span><span className="text-[#E8ECF4]">{c.joinedAt || c.joinedYearMonth}</span></div>
                  <div className="flex items-baseline justify-between border-b border-white/5 py-1"><span className="text-[#8B97AE]">{locale === "de" ? "Referenz (Admin-bestätigt)" : locale === "es" ? "Referencia (confirmada por administrador)" : "Reference (admin-confirmed)"}</span><span className="text-[#E8ECF4] tabular-nums">{(c.referenceVolumeConfirmed || 0).toLocaleString()}</span></div>
                  <div className="flex items-baseline justify-between border-b border-white/5 py-1"><span className="text-[#F5B841]">{locale === "de" ? "Angebotenes Volumen (Für 90 Tage)" : locale === "es" ? "Volumen ofrecido (por 90 días)" : "Offered volume (for 90 days)"}</span><span className="text-[#F5B841] tabular-nums">{(c.offeredVolume || 0).toLocaleString()}</span></div>
                  <div className="flex items-baseline justify-between border-b border-white/5 py-1"><span className="text-[#8B97AE]">Status</span><span className="text-[#E8ECF4]">{alreadyUnlocked ? "In contact" : "Available"}</span></div>
                  {c.preferredCasino && <div className="flex items-baseline justify-between border-b border-white/5 py-1"><span className="text-[#8B97AE]">Wants</span><span className="text-[#E8ECF4]">{c.preferredCasino}</span></div>}
                  {c.streamerPreferences?.length > 0 && (
                    <div className="flex items-baseline justify-between border-b border-white/5 py-1 gap-2">
                      <span className="text-[#8B97AE]">Rewards preference</span>
                      <span className="text-[#E8ECF4] text-right">{c.streamerPreferences.join(", ")}</span>
                    </div>
                  )}
                  {c.aboutMe && (
                    <div className="pt-1">
                      <div className="text-[#8B97AE] mb-1">About</div>
                      <div className="text-[#E8ECF4] whitespace-pre-wrap leading-relaxed" data-testid={`more-about-${c.id}`}>{c.aboutMe}</div>
                    </div>
                  )}
                  <div className="pt-1 text-[9px] uppercase tracking-widest text-[#8B97AE]">Non-PII pre-unlock view. Current casino + case site and a preferred/open-to-any indicator are shown; wallet, gaming account name, and the specific preferred operator remain hidden until unlock.</div>
                </div>
              </details>
              <Button
                data-testid={`unlock-${c.id}`}
                onClick={() => setConsentPlayer(c)}
                disabled={alreadyUnlocked || c.fullyBooked}
                className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 font-semibold transition-colors mt-auto disabled:opacity-40"
              >
                <Unlock className="w-4 h-4 mr-1.5" />
                {alreadyUnlocked
                  ? (locale === "de" ? "Bereits freigeschaltet" : locale === "es" ? "Ya desbloqueado" : "Already unlocked")
                  : c.fullyBooked
                    ? (locale === "de" ? "Vollständig belegt (5/5)" : locale === "es" ? "Completamente reservado (5/5)" : "Fully booked (5/5)")
                    : (locale === "de"
                        ? `Für ${((c.pricing && c.pricing.chatUnlockCharged) ?? c.unlockCostCredits ?? 0).toLocaleString()} Credits freischalten`
                        : locale === "es"
                        ? `Desbloquear por ${((c.pricing && c.pricing.chatUnlockCharged) ?? c.unlockCostCredits ?? 0).toLocaleString()} Créditos`
                        : `Unlock for ${((c.pricing && c.pricing.chatUnlockCharged) ?? c.unlockCostCredits ?? 0).toLocaleString()} Credits`)}
              </Button>
              {/* Iter 71 — live X/5 concurrent-unlock counter (server auth). */}
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest flex items-center justify-between" data-testid={`unlock-slots-${c.id}`}>
                <span className="text-[#8B97AE]">{locale === "de" ? "Aktive Freischaltungen" : locale === "es" ? "Desbloqueos activos" : "Active unlocks"}</span>
                <span className={c.fullyBooked ? "text-[#F26D6D]" : "text-[#E8ECF4]"}>{c.activeChatsCount ?? 0}/{c.chatCap ?? 5}</span>
              </div>
              {/* Iter 64 / Iter 77 — Streamer sees ONLY what they actually
                  pay: chat-unlock (now) + remainder (at contract signing).
                  The raw `commission` field is stripped server-side for
                  non-admin roles per the four hard compliance rules — VG's
                  internal margin is admin-only. No summed "full contract"
                  line is displayed. */}
              {!alreadyUnlocked && (() => {
                const p = c.pricing || {};
                const chat = Number(p.chatUnlockCharged ?? c.unlockCostCredits ?? 0);
                const remainder = Number(p.contractRemainder ?? 0);
                return (
                  <div className="mt-2 border border-white/10 bg-white/[0.02] p-2 space-y-1" data-testid={`price-breakdown-${c.id}`}>
                    <div className="flex items-center justify-between text-[11px] font-mono">
                      <span className="uppercase tracking-widest text-[#8B97AE]">{locale === "de" ? "Chat-Unlock (jetzt)" : locale === "es" ? "Chat-Unlock (ahora)" : "Chat unlock (now)"}</span>
                      <span className="text-[#2DD4A7]" data-testid={`price-chat-${c.id}`}>{chat.toLocaleString()} cr</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] font-mono">
                      <span className="uppercase tracking-widest text-[#8B97AE]">{locale === "de" ? "Rest bei Vertragsabschluss" : locale === "es" ? "Resto al firmar el contrato" : "At contract signing"}</span>
                      <span className="text-[#F5B841]" data-testid={`price-remainder-${c.id}`}>{remainder.toLocaleString()} cr</span>
                    </div>
                  </div>
                );
              })()}
            </article>
          );})}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between mt-8 border-t border-white/8 pt-6">
          <div className="text-xs font-mono text-[#8B97AE]" data-testid="marketplace-pageinfo">Page {data.page} of {data.totalPages} · {data.total} verified players</div>
          <div className="flex gap-2">
            <Button data-testid="page-prev" disabled={f.page <= 1} onClick={() => set("page", f.page - 1)} variant="ghost" className="rounded-none border border-white/15 h-9 px-4 text-[#E8ECF4] hover:bg-white/10 disabled:opacity-40 transition-colors">Prev</Button>
            <Button data-testid="page-next" disabled={f.page >= data.totalPages} onClick={() => set("page", f.page + 1)} variant="ghost" className="rounded-none border border-white/15 h-9 px-4 text-[#E8ECF4] hover:bg-white/10 disabled:opacity-40 transition-colors">Next</Button>
          </div>
        </div>
      )}

      <ConsentModal
        open={!!consentPlayer}
        player={consentPlayer}
        vertical={vertical}
        restrictionVersion={meta.restrictionVersion || "1"}
        onCancel={() => setConsentPlayer(null)}
        locale={locale}
        onConfirmed={() => {
          if (consentPlayer) {
            setUnlockedIds((old) => {
              const next = new Set(old);
              next.add(consentPlayer.id);
              return next;
            });
          }
          setConsentPlayer(null);
          // Refresh the list so the server-side `available` flag updates.
          load();
        }}
        t={t}
      />
    </div>
  );
}
